import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import Checkbox from './Checkbox';
import Form from './Form';
import Input from './Input';
import LinkButton from '../Buttons/LinkButton';
import Separator from '../Display/Separator';
import { handleKeyDown } from '../Helpers';

function ContactFormInfluencer({ company, className, lead, onPrivacyClick, isInquiringForSelf }, ref) {
    // Form State
    const [emptyFields, setEmptyFields] = useState([]);
    const [invalidFields, setInvalidFields] = useState([]);

    // Form Fields Refs
    const firstNameRef = useRef();
    const lastNameRef = useRef();
    const firstNameSecondaryRef = useRef();
    const lastNameSecondaryRef = useRef();
    const emailRef = useRef();
    const phoneRef = useRef();
    const smsRef = useRef();

    const formRefs = isInquiringForSelf
        ? [firstNameRef, lastNameRef, emailRef, phoneRef]
        : [firstNameRef, lastNameRef, emailRef, phoneRef, firstNameSecondaryRef, lastNameSecondaryRef];

    // Form Validation
    const validateEmail = email => {
        const EMAIL_REGEX = new RegExp(/^[\w-\.]+@([\[a-zA-Z]+\.)+[\w]{2,4}$/);
        const valid = EMAIL_REGEX.test(email);
        if (!valid) {
            setInvalidFields([...invalidFields, 'email']);
        } else {
            setInvalidFields(invalidFields.filter(field => field !== 'email'));
        }

        return valid;
    };

    const validateForm = () => {
        const invalidRefs = new Set();

        formRefs.map(ref => {
            const id = ref.current.id;
            !ref.current.value.length && invalidRefs.add(id);
        });

        setEmptyFields([...invalidRefs]);
        return ![...invalidRefs].length && validateEmail(emailRef.current.value);
    };

    const handleRequiredField = e => {
        const { id, value } = e.target;
        setEmptyFields(value === '' ? [...emptyFields, id] : emptyFields.filter(el => el !== id));
    };

    const getContact = () => {
        return {
            first_name: firstNameRef ? firstNameRef.current.value : undefined,
            last_name: lastNameRef ? lastNameRef.current.value : undefined,
            first_name_secondary: firstNameSecondaryRef ? firstNameSecondaryRef?.current?.value : undefined,
            last_name_secondary: lastNameSecondaryRef ? lastNameSecondaryRef?.current?.value : undefined,
            email: emailRef ? emailRef.current.value : undefined,
            phone_number: phoneRef ? phoneRef.current.value : undefined,
            is_agree_sms_notification: smsRef.current.checked ? 'Yes' : 'No'
        };
    };

    /* Exposed function to populate lead data in parent
     * This breaks the unidirectional data flow ethos of React
     * but the parent component allows users to navigate away
     * from the contact form before submitting.
     * This allows the parent to capture that event and persist
     * data entered into the form so the user doesn't lose progress
     * when they navigate back.
     */
    useImperativeHandle(ref, () => ({
        getContact,
        validateForm
    }));

    return (
        <>
            <Form id="clx-form__contact" className={`clx-form__contact ${className}`} onSubmit={validateForm}>
                <>
                    <h6 style={{ margin: '5px 2px' }}>
                        {isInquiringForSelf
                            ? "What's your name and contact information?"
                            : "What's your loved one's name?"}
                    </h6>
                    <Input id="first_name" label={isInquiringForSelf ? 'First name *' : "Loved one's first name *"}>
                        <input
                            id="first_name"
                            name="first_name"
                            className={emptyFields.includes('first_name') ? 'invalid' : ''}
                            type="text"
                            placeholder={isInquiringForSelf ? 'First Name' : "Loved one's first name"}
                            required
                            ref={firstNameRef}
                            onBlur={e => handleRequiredField(e)}
                            onChange={e => handleRequiredField(e)}
                            defaultValue={(lead && lead.first_name) || ''}
                            data-test-id="first_name"
                        />
                        {emptyFields.includes('first_name') && (
                            <span className="validation" tabIndex={emptyFields.includes('first_name') ? 0 : -1}>
                                {isInquiringForSelf ? 'First name is required' : "Loved one's first name is required"}
                            </span>
                        )}
                    </Input>
                    <Input
                        style={{ marginBottom: 5 }}
                        id="last_name"
                        label={isInquiringForSelf ? 'Last name *' : "Loved one's last name *"}
                    >
                        <input
                            id="last_name"
                            name="last_name"
                            className={emptyFields.includes('last_name') ? 'invalid' : ''}
                            type="text"
                            placeholder={isInquiringForSelf ? 'Last Name' : "Loved one's last name"}
                            required
                            ref={lastNameRef}
                            onBlur={e => handleRequiredField(e)}
                            onChange={e => handleRequiredField(e)}
                            defaultValue={(lead && lead.last_name) || ''}
                            data-test-id="first_name"
                        />
                        {emptyFields.includes('last_name') && (
                            <span className="validation" tabIndex={emptyFields.includes('last_name') ? 0 : -1}>
                                {isInquiringForSelf ? 'Last name is required' : "Loved one's last name is required"}
                            </span>
                        )}
                    </Input>
                </>

                {!isInquiringForSelf && (
                    <>
                        <h6 style={{ marginBottom: 5 }}>What's your name and contact information?</h6>
                        <Input id="first_name_secondary" label="Your first name *">
                            <input
                                id="first_name_secondary"
                                name="first_name_secondary"
                                className={emptyFields.includes('first_name_secondary') ? 'invalid' : ''}
                                type="text"
                                placeholder="Your first name"
                                required
                                ref={firstNameSecondaryRef}
                                onBlur={e => handleRequiredField(e)}
                                onChange={e => handleRequiredField(e)}
                                defaultValue={(lead && lead.first_name_secondary) || ''}
                                data-test-id="first_name_secondary"
                            />
                            {emptyFields.includes('first_name_secondary') && (
                                <span
                                    className="validation"
                                    tabIndex={emptyFields.includes('first_name_secondary') ? 0 : -1}
                                >
                                    First name is required
                                </span>
                            )}
                        </Input>
                        <Input id="last_name_secondary" label="Your last name">
                            <input
                                id="last_name_secondary"
                                name="last_name_secondary"
                                className={emptyFields.includes('last_name_secondary') ? 'invalid' : ''}
                                type="text"
                                placeholder="Your last name"
                                required
                                ref={lastNameSecondaryRef}
                                onBlur={e => handleRequiredField(e)}
                                onChange={e => handleRequiredField(e)}
                                defaultValue={(lead && lead.last_name_secondary) || ''}
                            />
                            {emptyFields.includes('last_name_secondary') && (
                                <span
                                    className="validation"
                                    tabIndex={emptyFields.includes('last_name_secondary') ? 0 : -1}
                                >
                                    Last name is required
                                </span>
                            )}
                        </Input>
                    </>
                )}
                <Input id="phone_number" label="Phone number *">
                    <input
                        id="phone_number"
                        name="phone_number"
                        className={emptyFields.includes('phone_number') ? 'invalid' : ''}
                        type="text"
                        placeholder="Phone number"
                        required
                        ref={phoneRef}
                        onBlur={e => handleRequiredField(e)}
                        onChange={e => handleRequiredField(e)}
                        defaultValue={(lead && lead.phone_number) || ''}
                    />
                    {emptyFields.includes('phone_number') && (
                        <span className="validation" tabIndex={emptyFields.includes('phone_number') ? 0 : -1}>
                            Phone number is required
                        </span>
                    )}
                </Input>
                <Input id="email" label="Email *">
                    <input
                        id="email"
                        name="email"
                        className={emptyFields.includes('email') || invalidFields.includes('email') ? 'invalid' : ''}
                        type="email"
                        placeholder="Email"
                        required
                        ref={emailRef}
                        onBlur={e => handleRequiredField(e)}
                        onChange={e => handleRequiredField(e)}
                        defaultValue={(lead && lead.email) || ''}
                    />
                    {emptyFields.includes('email') && (
                        <span className="validation" tabIndex={emptyFields.includes('email') ? 0 : -1}>
                            Email is required
                        </span>
                    )}
                    {invalidFields.includes('email') && (
                        <span className="validation" tabIndex={invalidFields.includes('email') ? 0 : -1}>
                            Please enter a valid email.
                        </span>
                    )}
                </Input>
                <Checkbox
                    id="is_agree_sms_notification"
                    label={`I agree to receive SMS messages from ${company}. Msg & data rates may apply.`}
                    labelSize="small"
                >
                    <input
                        type="checkbox"
                        id="is_agree_sms_notification"
                        name="is_agree_sms_notification"
                        ref={smsRef}
                        defaultChecked={lead && lead.is_agree_sms_notification === 'Yes'}
                    />
                </Checkbox>
            </Form>
            <div className="clx-privacy" style={{ textAlign: 'center' }}>
                <Separator />
                <LinkButton
                    label="Consumer Privacy Notice"
                    onClick={onPrivacyClick}
                    onKeyDown={() => handleKeyDown(onPrivacyClick)}
                />
            </div>
        </>
    );
}

export default forwardRef(ContactFormInfluencer);
